import { Project, ProjectModel, ProjectsState } from 'src/store/state/projects';
import { ReduxState } from 'src/store/types';
import { getPrefixFromNamespace } from 'src/utils/getPrefixFromNamespace';

type OldProjectModel = Omit<ProjectModel, 'prefix' | 'shouldPublishPrefix'>;

type OldProject = Omit<Project, 'modelsById'> & {
  modelsById: Record<string, OldProjectModel>;
};

/**
 * Migration to add `ProjectModel.prefix` and `ProjectModel.shouldPublishPrefix`.
 * The `prefix` is based on the existing model namespace, while `shouldPublishPrefix`
 * defaults to true. Prefixes are made to be unique following the migration.
 *  */
// prevState must be typed as `any` since it's an older version of the Redux State
export const v51 = (prevState: any): ReduxState => {
  const nextState = { ...prevState };

  if (nextState.projects) {
    nextState.projects = v51Projects(prevState.projects);
  }

  return nextState;
};

const v51Projects = (prevProjectsState: any): ProjectsState => {
  const projectsById: Record<string, Project> = {};

  Object.entries(
    prevProjectsState.projectsById as Record<string, OldProject>
  ).forEach(([projectId, { modelsById, ...project }]) => {
    const usedPrefixes = new Set<string>();
    const getUniquePrefix = (prefix: string) => {
      let fixedPrefix = prefix;
      let num = 0;
      while (usedPrefixes.has(fixedPrefix)) {
        num += 1;
        fixedPrefix = `${prefix}${num}`;
      }

      usedPrefixes.add(fixedPrefix);
      return fixedPrefix;
    };
    const updatedModelsById = Object.entries(modelsById).reduce<
      Record<string, ProjectModel>
    >((acc, [modelId, model]) => {
      const initialPrefix = getPrefixFromNamespace(model.namespace);
      const uniquePrefix = getUniquePrefix(initialPrefix);

      const updatedModel: ProjectModel = {
        ...model,
        prefix: uniquePrefix,
        shouldPublishPrefix: true,
      };
      acc[modelId] = updatedModel;
      return acc;
    }, {});

    projectsById[projectId] = { ...project, modelsById: updatedModelsById };
  });

  return { ...prevProjectsState, projectsById };
};
